import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Row,
  Col,
  Pagination,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaEdit, FaTrash, FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import moment from 'moment';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { COBRANCA_IMOVEL } from '~/constant';
import AuthStorage from '~/Utils/AuthStorage';
import { extrairMensagemErro } from '~/Utils/Helpers';
import {
  listarIsencoesCobranca,
  listarGruposEmpresaIsencao,
  excluirIsencaoCobranca,
} from '~/Services/Service';
import FiltrosIsencao from './FiltrosIsencao';
import ModalIncluirIsencao from './ModalIncluirIsencao';
import ModalEditarIsencao from './ModalEditarIsencao';
import ModalExcluirIsencao from './ModalExcluirIsencao';

const PAGE_SIZE = 20;

const thSortStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

const FILTROS_INICIAL = {
  codigo_usuario: null,
  validade_inicio: '',
  validade_fim: '',
  codigo_grupo_empresa: null,
  codigo_conta: null,
};

// Comparador genérico para sort local — trata null, number, string e data ISO
const compararValores = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'pt-BR', { numeric: true, sensitivity: 'base' });
};

const IsencaoCobranca = () => {
  // Lista completa retornada pelo backend (já filtrada pelos filtros do backend)
  const [isencoes, setIsencoes] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [loading, setLoading] = useState(false);

  const [filtros, setFiltros] = useState(FILTROS_INICIAL);
  const [sort, setSort] = useState('apelido_conta');
  const [dir, setDir] = useState('asc');

  const [geOptions, setGeOptions] = useState([]);

  const [showModalIncluir, setShowModalIncluir] = useState(false);
  const [showModalEditar, setShowModalEditar] = useState(false);
  const [itemEditar, setItemEditar] = useState(null);
  const [showModalExcluir, setShowModalExcluir] = useState(false);
  const [itenExcluir, setItemExcluir] = useState([]);

  // Dispara refetch da lista de usuários do filtro após mutações
  const [reloadUsuariosKey, setReloadUsuariosKey] = useState(0);
  const recarregarUsuariosFiltro = () => setReloadUsuariosKey((k) => k + 1);

  const temPermissao = AuthStorage.temCaracteristica(COBRANCA_IMOVEL);

  // Filtros locais (GE e Conta) aplicados sobre a lista já recebida do backend
  const isencoesFiltradas = useMemo(() => {
    return isencoes.filter((item) => {
      if (
        filtros.codigo_grupo_empresa != null &&
        item.codigo_grupo_empresa !== filtros.codigo_grupo_empresa
      ) {
        return false;
      }
      if (
        filtros.codigo_conta != null &&
        item.codigo_conta !== filtros.codigo_conta
      ) {
        return false;
      }
      return true;
    });
  }, [isencoes, filtros.codigo_grupo_empresa, filtros.codigo_conta]);

  // Opções do dropdown de Conta — distintas da lista atual filtrada pelo GE selecionado
  const contasOptions = useMemo(() => {
    if (filtros.codigo_grupo_empresa == null) return [];
    const seen = new Set();
    const opts = [];
    isencoes.forEach((item) => {
      if (item.codigo_grupo_empresa !== filtros.codigo_grupo_empresa) return;
      if (seen.has(item.codigo_conta)) return;
      seen.add(item.codigo_conta);
      opts.push({
        value: item.codigo_conta,
        label: item.apelido_conta || String(item.codigo_conta),
      });
    });
    return opts;
  }, [isencoes, filtros.codigo_grupo_empresa]);

  // Lista filtrada e ordenada (sort local)
  const isencoesOrdenadas = useMemo(() => {
    const arr = [...isencoesFiltradas];
    arr.sort((a, b) => {
      const cmp = compararValores(a[sort], b[sort]);
      return dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [isencoesFiltradas, sort, dir]);

  // Slice da página atual
  const total = isencoesOrdenadas.length;
  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isencoesPagina = useMemo(() => {
    const start = (pagina - 1) * PAGE_SIZE;
    return isencoesOrdenadas.slice(start, start + PAGE_SIZE);
  }, [isencoesOrdenadas, pagina]);

  // Carrega GEs do usuário logado — usado pelo filtro de GE e pelo modal de inclusão
  useEffect(() => {
    listarGruposEmpresaIsencao()
      .then((res) => {
        const lista = res || [];
        const opts = lista.map((g) => ({
          value: g.codigo_grupo_empresa,
          label: `${g.codigo_grupo_empresa} - ${g.apelido || ''}`.trim(),
        }));
        setGeOptions(opts);
      })
      .catch(() => {
        // silencioso — lista fica vazia
      });
  }, []);

  const carregarIsencoes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtros.codigo_usuario) params.codigoUsuario = filtros.codigo_usuario;
      if (filtros.validade_inicio) params.validadeInicio = filtros.validade_inicio;
      if (filtros.validade_fim) params.validadeFim = filtros.validade_fim;

      const resp = await listarIsencoesCobranca(params);
      setIsencoes(Array.isArray(resp) ? resp : []);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
      setIsencoes([]);
    } finally {
      setLoading(false);
    }
  }, [filtros.codigo_usuario, filtros.validade_inicio, filtros.validade_fim]);

  // Recarrega quando filtros de backend mudam (GE e Conta são client-side, não disparam fetch)
  useEffect(() => {
    setPagina(1);
    carregarIsencoes();
  }, [carregarIsencoes]);

  // Clamp da página quando a lista encolhe (ex: exclusão do último item)
  useEffect(() => {
    if (pagina > totalPaginas) setPagina(totalPaginas);
  }, [totalPaginas, pagina]);

  const handleFiltrosChange = (novosFiltros) => {
    // Trocar GE invalida a conta selecionada (Conta é dependente do GE)
    if (novosFiltros.codigo_grupo_empresa !== filtros.codigo_grupo_empresa) {
      novosFiltros = { ...novosFiltros, codigo_conta: null };
    }
    setFiltros(novosFiltros);
    setPagina(1);
  };

  const handleOrdenacao = (campo) => {
    if (sort === campo) {
      setDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(campo);
      setDir('asc');
    }
    setPagina(1);
  };

  const iconeOrdenacao = (campo) => {
    if (sort === campo && dir === 'asc') return <FaSortUp style={{ marginLeft: 4 }} />;
    if (sort === campo && dir === 'desc') return <FaSortDown style={{ marginLeft: 4 }} />;
    return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
  };

  const handlePagina = (pg) => {
    if (pg < 1 || pg > totalPaginas) return;
    setPagina(pg);
  };

  // Edição individual (linha) — altera a validade da isenção
  const handleEditarIndividual = (item) => {
    setItemEditar(item);
    setShowModalEditar(true);
  };

  // Exclusão individual (linha)
  const handleExcluirIndividual = (item) => {
    setItemExcluir([item]);
    setShowModalExcluir(true);
  };

  const handleExcluirSucesso = () => {
    setShowModalExcluir(false);
    setItemExcluir([]);
    carregarIsencoes();
    recarregarUsuariosFiltro();
  };

  const handleIncluirSucesso = () => {
    setShowModalIncluir(false);
    carregarIsencoes();
    recarregarUsuariosFiltro();
  };

  const handleEditarSucesso = () => {
    setShowModalEditar(false);
    setItemEditar(null);
    carregarIsencoes();
    recarregarUsuariosFiltro();
  };

  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const pages = [];
    const maxBtn = 5;
    let start = Math.max(1, pagina - Math.floor(maxBtn / 2));
    const end = Math.min(totalPaginas, start + maxBtn - 1);
    if (end - start < maxBtn - 1) start = Math.max(1, end - maxBtn + 1);

    pages.push(
      <Pagination.First key="first" disabled={pagina === 1} onClick={() => handlePagina(1)} />,
      <Pagination.Prev key="prev" disabled={pagina === 1} onClick={() => handlePagina(pagina - 1)} />
    );
    if (start > 1) pages.push(<Pagination.Ellipsis key="ell-start" disabled />);
    for (let pg = start; pg <= end; pg += 1) {
      pages.push(
        <Pagination.Item key={pg} active={pg === pagina} onClick={() => handlePagina(pg)}>
          {pg}
        </Pagination.Item>
      );
    }
    if (end < totalPaginas) pages.push(<Pagination.Ellipsis key="ell-end" disabled />);
    pages.push(
      <Pagination.Next key="next" disabled={pagina === totalPaginas} onClick={() => handlePagina(pagina + 1)} />,
      <Pagination.Last key="last" disabled={pagina === totalPaginas} onClick={() => handlePagina(totalPaginas)} />
    );

    return (
      <Row className="align-items-center mt-2">
        <Col xs={12} md={6}>
          <small className="text-muted">
            Mostrando {Math.min((pagina - 1) * PAGE_SIZE + 1, total)} até{' '}
            {Math.min(pagina * PAGE_SIZE, total)} de {total} registro{total !== 1 ? 's' : ''}
          </small>
        </Col>
        <Col xs={12} md={6} className="d-flex justify-content-md-end">
          <Pagination size="sm" className="mb-0">
            {pages}
          </Pagination>
        </Col>
      </Row>
    );
  };

  return (
    <Container fluid>
      <Header title="Clientes Isentos de Cobrança" />

      {/* Card Filtros */}
      <Card className="mb-3">
        <Card.Body>
          <FiltrosIsencao
            filtros={filtros}
            onChange={handleFiltrosChange}
            loading={loading}
            podeIncluir={temPermissao}
            onIncluir={() => setShowModalIncluir(true)}
            reloadUsuariosKey={reloadUsuariosKey}
            geOptions={geOptions}
            contasOptions={contasOptions}
          />
        </Card.Body>
      </Card>

      {/* Grid */}
      <Card>
        <Card.Body className="p-0">
          {loading && <Loading />}
          <div className="table-responsive">
            <Table striped bordered hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th style={thSortStyle} onClick={() => handleOrdenacao('apelido_conta')}>
                    Conta {iconeOrdenacao('apelido_conta')}
                  </th>
                  <th style={thSortStyle} onClick={() => handleOrdenacao('codigo_grupo_empresa')}>
                    GE {iconeOrdenacao('codigo_grupo_empresa')}
                  </th>
                  <th style={thSortStyle} onClick={() => handleOrdenacao('nome_usuario')}>
                    Usuário {iconeOrdenacao('nome_usuario')}
                  </th>
                  <th style={thSortStyle} onClick={() => handleOrdenacao('validade')}>
                    Validade {iconeOrdenacao('validade')}
                  </th>
                  <th style={{ width: 96 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {!loading && isencoesPagina.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-3">
                      Nenhuma isenção encontrada.
                    </td>
                  </tr>
                )}
                {isencoesPagina.map((item) => (
                  <tr key={`${item.codigo_grupo_empresa}-${item.codigo_conta}`}>
                    <td>{item.apelido_conta || item.codigo_conta}</td>
                    <td>{item.codigo_grupo_empresa}</td>
                    <td>{item.nome_usuario || item.codigo_usuario}</td>
                    <td>
                      {item.validade
                        ? moment(item.validade).format('DD/MM/YYYY')
                        : '—'}
                    </td>
                    <td>
                      {temPermissao && (
                        <>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="mr-1"
                            onClick={() => handleEditarIndividual(item)}
                            title="Editar isenção"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleExcluirIndividual(item)}
                            title="Excluir isenção"
                          >
                            <FaTrash />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
        {!loading && isencoesPagina.length > 0 && (
          <Card.Footer>{renderPaginacao()}</Card.Footer>
        )}
      </Card>

      {/* Modais */}
      {showModalIncluir && (
        <ModalIncluirIsencao
          show={showModalIncluir}
          geOptions={geOptions}
          onHide={() => setShowModalIncluir(false)}
          onSucesso={handleIncluirSucesso}
        />
      )}

      {showModalEditar && (
        <ModalEditarIsencao
          show={showModalEditar}
          isencao={itemEditar}
          onHide={() => { setShowModalEditar(false); setItemEditar(null); }}
          onSucesso={handleEditarSucesso}
        />
      )}

      {showModalExcluir && (
        <ModalExcluirIsencao
          show={showModalExcluir}
          itens={itenExcluir}
          onHide={() => { setShowModalExcluir(false); setItemExcluir([]); }}
          onSucesso={handleExcluirSucesso}
        />
      )}
    </Container>
  );
};

export default IsencaoCobranca;
